// KLUB-88 (#282) — leitura de data vinda do banco.
//
// O banco devolve DUAS formas, e só uma delas carrega hora:
//
//   timestamptz → "2026-08-13T14:30:00+00:00"  um INSTANTE
//   date        → "2026-08-13"                 um DIA
//
// O parser do ECMAScript trata as duas de modo oposto: a forma só-data é lida
// como meia-noite UTC, enquanto data-com-hora sem offset é lida como hora
// local. Formatar a primeira em fuso negativo recua um dia — em São Paulo
// (UTC-3), "2026-08-13" vira 12/08/2026. Foi assim que o "Membro desde" passou
// a mostrar a véspera de `profiles.contract_start`, no admin E na carteirinha.
//
// Pinar a forma só-data à meia-noite LOCAL desfaz a assimetria: o dia
// atravessa a formatação intacto, em qualquer fuso.
//
// Por que no pacote compartilhado, e não em cada app: o defeito é do PARSER, e
// o parser é anônimo — cada tela escreve `new Date(x)` e herda a armadilha sem
// saber. As mesmas colunas (`contract_start`, `birthday`) são desenhadas pelo
// admin e pelo mobile, e uma correção só de um lado deixaria a outra metade
// mostrando a véspera da mesma data.

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Se o valor é um DIA (coluna `date`) em vez de um instante. */
export function isDateOnly(value: string): boolean {
  return DATE_ONLY.test(value);
}

/**
 * Lê uma data do banco respeitando o que ela é: um dia continua sendo aquele
 * dia; um instante continua sendo aquele instante.
 */
export function parseDbDate(value: string): Date {
  return new Date(isDateOnly(value) ? `${value}T00:00:00` : value);
}

/**
 * Formata pelo locale do tenant. Substitui `new Date(x).toLocaleDateString(…)`
 * nas telas — é a mesma chamada, com o parser correto atrás.
 *
 * `timeZone` é IGNORADO para a forma só-data, de propósito. Um dia não tem
 * fuso: converter "2026-08-13" para o fuso do clube é uma troca de categoria,
 * e reintroduz exatamente o recuo que o pinamento desfez sempre que o relógio
 * de quem lê difere do relógio do clube. Para um instante, `timeZone` continua
 * valendo integralmente — é o que decide em que dia aquele instante caiu.
 */
export function formatDateLocale(
  value: string,
  locale: string,
  options?: Intl.DateTimeFormatOptions
): string {
  if (isDateOnly(value) && options?.timeZone) {
    const { timeZone: _ignored, ...rest } = options;
    return parseDbDate(value).toLocaleDateString(locale, rest);
  }
  return parseDbDate(value).toLocaleDateString(locale, options);
}

/**
 * Formato brasileiro fixo (dd/mm/aaaa), independente do locale do tenant.
 * Montado à mão em vez de `toLocaleDateString` porque o formato aqui é do
 * domínio, não da preferência de quem lê.
 */
export function formatDateBR(value: string): string {
  const d = parseDbDate(value);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}
